import React from 'react';

import PropTypes from 'prop-types';
import { FaPhone, FaEnvelope, FaUser } from 'react-icons/fa';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { useTranslation } from 'react-i18next';

import koord1 from '../../Assets/img/koord1.webp';
import koord2 from '../../Assets/img/koord2.webp';
import koord3 from '../../Assets/img/koord3.webp';
import WithBackground from '../Common/WithBackground';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import './style.css';

const WorkerCoordinators = ({ bg, overlay }) => {
  const { t } = useTranslation();

  const coordinators = [
    {
      id: 1,
      name: t('workerCoordinators.members.mykolaOrydzhuk.name'),
      position: t('workerCoordinators.members.mykolaOrydzhuk.position'),
      phone: t('workerCoordinators.members.mykolaOrydzhuk.phone'),
      email: t('workerCoordinators.members.mykolaOrydzhuk.email'),
      facilities: t('workerCoordinators.members.mykolaOrydzhuk.facilities'),
      photo: koord1,
      callButton: t('workerCoordinators.members.mykolaOrydzhuk.callButton'),
    },
    {
      id: 2,
      name: t('workerCoordinators.members.olenaKatrenko.name'),
      position: t('workerCoordinators.members.olenaKatrenko.position'),
      phone: t('workerCoordinators.members.olenaKatrenko.phone'),
      email: t('workerCoordinators.members.olenaKatrenko.email'),
      facilities: t('workerCoordinators.members.olenaKatrenko.facilities'),
      photo: koord2,
      callButton: t('workerCoordinators.members.olenaKatrenko.callButton'),
    },
    {
      id: 3,
      name: t('workerCoordinators.members.andriiOrobets.name'),
      position: t('workerCoordinators.members.andriiOrobets.position'),
      phone: t('workerCoordinators.members.andriiOrobets.phone'),
      email: t('workerCoordinators.members.andriiOrobets.email'),
      facilities: t('workerCoordinators.members.andriiOrobets.facilities'),
      photo: koord3,
      callButton: t('workerCoordinators.members.andriiOrobets.callButton'),
    },
    {
      id: 4,
      name: t('workerCoordinators.members.alinaMatika.name'),
      position: t('workerCoordinators.members.alinaMatika.position'),
      phone: t('workerCoordinators.members.alinaMatika.phone'),
      email: t('workerCoordinators.members.alinaMatika.email'),
      facilities: t('workerCoordinators.members.alinaMatika.facilities'),
      photo: null,
      callButton: t('workerCoordinators.members.alinaMatika.callButton'),
    },
  ];

  const coordinatorsContent = (
    <section className='worker-coordinators'>
      <div className='worker-coordinators__container'>
        <div className='worker-coordinators__header'>
          <h2 className='worker-coordinators__title'>
            {t('workerCoordinators.title')}
          </h2>
          <p className='worker-coordinators__intro'>
            {t('workerCoordinators.intro')}
          </p>
        </div>

        <div className='worker-coordinators__swiper-container'>
          <Swiper
            modules={[Navigation, Pagination]}
            navigation={{
              nextEl: '.worker-coordinators__next',
              prevEl: '.worker-coordinators__prev',
            }}
            pagination={{
              el: '.worker-coordinators__pagination',
              clickable: true,
            }}
            spaceBetween={24}
            breakpoints={{
              0: { slidesPerView: 1, spaceBetween: 16 },
              769: { slidesPerView: 2, spaceBetween: 20 },
              1025: { slidesPerView: 3, spaceBetween: 24 },
            }}
            className='worker-coordinators-swiper'
          >
            {coordinators.map((coordinator) => (
              <SwiperSlide key={coordinator.id}>
                <div className='coordinator-card'>
                  <div className='coordinator-card__photo'>
                    {coordinator.photo ? (
                      <img
                        src={coordinator.photo}
                        alt={coordinator.name}
                        className='coordinator-card__image'
                      />
                    ) : (
                      <div className='coordinator-card__placeholder'>
                        <FaUser />
                      </div>
                    )}
                  </div>

                  <div className='coordinator-card__content'>
                    <h3 className='coordinator-card__name'>
                      {coordinator.name}
                    </h3>
                    <p className='coordinator-card__position'>
                      {coordinator.position}
                    </p>

                    {coordinator.facilities && (
                      <div className='coordinator-card__facilities'>
                        <p className='coordinator-card__facilities-text'>
                          {coordinator.facilities}
                        </p>
                      </div>
                    )}

                    <div className='coordinator-card__contact'>
                      <div className='coordinator-card__contact-item'>
                        <FaPhone />
                        <a
                          href={`tel:${coordinator.phone}`}
                          className='coordinator-card__contact-link'
                        >
                          {coordinator.phone}
                        </a>
                      </div>
                      <div className='coordinator-card__contact-item'>
                        <FaEnvelope />
                        <a
                          href={`mailto:${coordinator.email}`}
                          className='coordinator-card__contact-link'
                        >
                          {coordinator.email}
                        </a>
                      </div>
                    </div>

                    <div className='coordinator-card__action'>
                      <a
                        href={`tel:${coordinator.phone}`}
                        className='coordinator-card__btn'
                      >
                        <FaPhone />
                        {coordinator.callButton}
                      </a>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>

          <div className='worker-coordinators__navigation'>
            <button
              className='worker-coordinators__prev'
              aria-label='Previous coordinator'
            >
              <svg width='20' height='20' viewBox='0 0 24 24' fill='none'>
                <path
                  d='M15 18L9 12L15 6'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </button>

            <button
              className='worker-coordinators__next'
              aria-label='Next coordinator'
            >
              <svg width='20' height='20' viewBox='0 0 24 24' fill='none'>
                <path
                  d='M9 18L15 12L9 6'
                  stroke='currentColor'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </button>
          </div>

          <div className='worker-coordinators__pagination'></div>
        </div>

        <div className='worker-coordinators__housing'>
          <span className='worker-coordinators__housing-label'>
            {t('housingCoordinator.label')}:
          </span>
          <a
            href={`tel:${t('housingCoordinator.phone').replace(/\s/g, '')}`}
            className='worker-coordinators__housing-phone'
          >
            {t('housingCoordinator.phone')}
          </a>
        </div>
      </div>
    </section>
  );

  return bg ? (
    <WithBackground bg={bg} overlay={overlay}>
      {coordinatorsContent}
    </WithBackground>
  ) : (
    coordinatorsContent
  );
};

WorkerCoordinators.propTypes = {
  bg: PropTypes.string,
  overlay: PropTypes.oneOf(['dark', 'light', 'golden', 'none']),
};

WorkerCoordinators.defaultProps = {
  bg: null,
  overlay: 'dark',
};

export default WorkerCoordinators;
